import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

let config = {
  margin: 10,
  borderMagin: 3,
  fontSize: 7.2,
  bordergap: 3,
  currentY: 0,
};

const generateStaticSection = (doc, formInfo, inputStates) => {
  const is13 = formInfo?.name?.includes("13");
  const bgColor = is13 ? "#4473c5" : "#ed7d31";
  const textColor = is13 ? "#ffffff" : "#000000";

  const parsedTitle = formInfo?.title ? JSON.parse(formInfo.title) : {};
  const rule = parsedTitle?.rule || "";
  const title = parsedTitle?.title || "";

  // First row - Title section with background color
  autoTable(doc, {
    startY: 10,
    body: [
      [
        {
          content: (formInfo?.name || "").toUpperCase(),
          styles: {
            fillColor: bgColor,
            textColor: textColor,
            halign: "center",
            valign: "middle",
            fontStyle: "bold",
            fontSize: config.fontSize,
            cellPadding: { top: 7, bottom: 2, left: 5, right: 5 },
          },
        },
      ],
      [
        {
          content: rule,
          styles: {
            fillColor: bgColor,
            textColor: textColor,
            halign: "center",
            valign: "middle",
            fontStyle: "bold",
            fontSize: config.fontSize,
            cellPadding: { top: 2, bottom: 2, left: 5, right: 5 },
          },
        },
      ],
      [
        {
          content: title,
          styles: {
            fillColor: bgColor,
            textColor: textColor,
            halign: "center",
            valign: "middle",
            fontStyle: "bolditalic", // applies bold and italic
            fontSize: config.fontSize,
            cellPadding: { top: 2, bottom: 5, left: 5, right: 5 },
          },
        },
      ],
    ],
    theme: "plain",
    margin: { left: config.margin, right: config.margin },
  });

  // Second TableRow: Report number, right-aligned
  autoTable(doc, {
    startY: doc.lastAutoTable?.finalY + 2,
    margin: { left: config.margin, right: config.margin },
    theme: "plain",
    body: [
      [
        {
          content: `Report No: ${inputStates?.reportNumber || ""}`,
          colSpan: 4,
          styles: {
            halign: "right",
            fontStyle: "bold",
            fontSize: config.fontSize - 1,
            cellPadding: { top: 2, bottom: 2, left: 0, right: 2 },
          },
        },
      ],
    ],
  });
};

const generateDynamicTableSection = (
  doc,
  filteredInputFields,
  inputStates,
  Factory,
  Shop,
  Product
) => {
  const body = [];

  filteredInputFields?.forEach((field) => {
    if (field?.stateKey === "companyName") return;

    let label = field?.label || "";
    const numberMatch = label.match(/^\d+(\.\s|[a-z]\) )/);
    const number = numberMatch ? numberMatch[0].trim() : "";
    let restLabel = label.replace(number, "").trim();
    if (restLabel === "Address of the Factory") {
      restLabel = "Name and address of the Factory";
    }

    let value = "";

    if (field?.stateKey === "addressFactory") {
      const factory = Factory?.find((ele) => ele.id === inputStates?.factoryId);
      value = `${factory?.name || ""}\n${inputStates?.addressFactory || ""}`;
    } else if (field?.stateKey === "shopId") {
      const shop = Shop?.find((ele) => ele.id === inputStates?.shopId);
      value = shop?.name || "";
    } else if (field?.stateKey === "equipmentId") {
      const product = Product?.find(
        (ele) => ele.id === inputStates?.equipmentId
      );
      value = product?.name || "";
    } else if (field?.type === "DatePicker") {
      const date = inputStates?.[field?.stateKey];
      value = date ? new Date(date).toLocaleDateString("en-GB") : "";
    } else {
      value = inputStates?.[field?.stateKey] || "";
    }

    body.push([
      {
        content: number,
        styles: {
          fillColor: "#d9d9d9",
          halign: "center",
          valign: "middle",
          fontSize: config.fontSize,
          cellPadding: { top: 2, bottom: 2 },
        },
      },
      {
        content: restLabel,
        styles: {
          fillColor: "#fff2cd",
          halign: "left",
          valign: "middle",
          fontSize: config.fontSize,
          cellPadding: { top: 2, bottom: 2, left: 5 },
        },
      },
      {
        content: ":",
        styles: {
          fillColor: "#d9d9d9",
          halign: "center",
          valign: "middle",
          fontSize: config.fontSize,
          cellPadding: { top: 2, bottom: 2 },
        },
      },
      {
        content: value,
        styles: {
          fillColor: "#fff2cd",
          halign: "left",
          valign: "middle",
          fontSize: config.fontSize,
          cellPadding: { top: 2, bottom: 2, left: 5 },
        },
      },
    ]);
  });

  // Calculate the total available width of the table
  const totalPageWidth = doc.internal.pageSize.getWidth() - config.margin * 2;
  const columnPercentages = [2, 48, 2, 48];
  // Calculate the actual column widths based on percentages
  const columnWidths = columnPercentages.map((percentage) => {
    return (totalPageWidth * percentage) / 100;
  });

  autoTable(doc, {
    startY: doc.lastAutoTable?.finalY + 2,
    margin: { left: config.margin, right: config.margin },
    body: body,
    theme: "plain",
    styles: {
      fontSize: config.fontSize,
      textColor: 0,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      valign: "middle",
      cellPadding: { top: 5, left: 5, bottom: 5, right: 5 },
    },
    columnStyles: {
      0: { cellWidth: columnWidths[0] },
      1: { cellWidth: columnWidths[1] },
      2: { cellWidth: columnWidths[2] },
      3: { cellWidth: columnWidths[3] },
    },
  });
};

const generateSignatureSectionPDF = async ({
  doc,
  inputStates,
  selectedOption,
  formInfo,
}) => {
  const getFormattedDate = () => {
    const today = inputStates?.reportDate
      ? new Date(inputStates?.reportDate)
      : new Date();
    return today.toLocaleDateString("en-GB");
  };

  const getNextYearDate = () => {
    return inputStates?.certificateValidity
      ? moment(inputStates?.certificateValidity).format("DD/MM/YYYY")
      : "";
  };

  const paragraphText =
    formInfo?.name !== "Form13"
      ? `I/We certify that on ${getFormattedDate()} I/ We thoroughly examined the hoist, lift, lifting machine, ropes and lifting tackles and details of examinations / test carried out is enclosed herewith that the above is a correct report of the result.`
      : `I/We certify that on ${getFormattedDate()} the pressure plant / vessel described above was thoroughly cleaned and (so far as its construction permits) made accessible for thorough examination and for such tests as were necessary for thorough examination and that on the said date. I thoroughly examined this pressure vessel/Plant including its fittings and that the above is true report of my examination.`;

  const officeAddress =
    "3SM TECH SOLUTION\nOffice # 301, Samyak Galleria, Near Divisional Sports Complex,\nSutgirani Chowk, Garkheda Rd, Aurangabad, Maharashtra - 431009";
  const finalY = doc.lastAutoTable.finalY + 2;
  autoTable(doc, {
    startY: finalY,
    margin: { left: config.margin, right: config.margin },
    body: [
      [
        {
          content: paragraphText,
          styles: {
            halign: "justify",
            cellPadding: 2,
          },
        },
      ],
      [
        {
          content: `Next Due Date: ${getNextYearDate()}`,
          styles: {
            halign: "justify",
            cellPadding: 1,
            fontStyle: "bold",
            fontSize: config.fontSize - 1,
          },
        },
      ],
    ],
    theme: "plain",
    styles: {
      fontSize: config.fontSize,
    },
  });

  // Calculate the total available width of the table
  const totalPageWidth = doc.internal.pageSize.getWidth() - config.margin * 2;
  const columnPercentages = [20, 29, 2, 29, 20];
  // Calculate the actual column widths based on percentages
  const columnWidths = columnPercentages.map((percentage) => {
    return (totalPageWidth * percentage) / 100;
  });

  // Prepare autoTable
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 3,
    margin: { left: config.margin, right: config.margin },
    body: [
      [
        {
          content: officeAddress,
          rowSpan: 6,
          styles: { fillColor: "#d9d9d9", halign: "left" },
        },
        {
          content: "Signature of Competent Person",
          styles: {
            fillColor: "#fff2cd",
            halign: "right",
            fontSize: config.fontSize - 1,
            fontStyle: "bold",
          },
        },
        { content: ":", styles: { fillColor: "#d9d9d9", halign: "center" } },
        {
          content: selectedOption?.signPath ? "" : "",
          styles: {
            minCellHeight: 20,
          },
        },
        {
          content: selectedOption?.stampPath ? "" : "",
          rowSpan: 5,
        },
      ],
      [
        {
          content: "Name of Competent Person",
          styles: {
            fillColor: "#fff2cd",
            halign: "right",
            fontSize: config.fontSize - 1,
            fontStyle: "bold",
          },
        },
        { content: ":", styles: { fillColor: "#d9d9d9", halign: "center" } },
        { content: selectedOption?.personName || "" },
      ],
      [
        {
          content: "Qualification",
          styles: {
            fillColor: "#fff2cd",
            halign: "right",
            fontSize: config.fontSize - 1,
            fontStyle: "bold",
          },
        },
        { content: ":", styles: { fillColor: "#d9d9d9", halign: "center" } },
        { content: selectedOption?.qualification || "" },
      ],
      [
        {
          content: "Address",
          styles: {
            fillColor: "#fff2cd",
            halign: "right",
            fontSize: config.fontSize - 1,
            fontStyle: "bold",
          },
        },
        { content: ":", styles: { fillColor: "#d9d9d9", halign: "center" } },
        { content: selectedOption?.address || "" },
      ],
      [
        {
          content: "Date",
          styles: {
            fillColor: "#fff2cd",
            halign: "right",
            fontSize: config.fontSize - 1,
            fontStyle: "bold",
          },
        },
        { content: ":", styles: { fillColor: "#d9d9d9", halign: "center" } },
        { content: getFormattedDate() },
      ],
    ],
    theme: "plain",
    styles: {
      fontSize: config.fontSize,
      cellPadding: { top: 2, left: 5, bottom: 2, right: 5 },
      valign: "middle",
      textColor: 0,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: columnWidths[0] },
      1: { cellWidth: columnWidths[1] },
      2: { cellWidth: columnWidths[2] },
      3: { cellWidth: columnWidths[3] },
      4: { cellWidth: columnWidths[4] },
    },
    didDrawCell: (data) => {
      if (data.column.index === 4 && selectedOption?.stampPath) {
        const { x, y, width, height } = data.cell;
        const imageSize = height - 10;
        const centerX = x + (width - imageSize) / 2;
        const centerY = y + (height - imageSize) / 2;

        doc.addImage(
          selectedOption?.stampPath || "",
          "PNG",
          centerX,
          centerY,
          imageSize,
          imageSize
        );
      }
      // sign img
      if (
        data.column.index === 3 &&
        data.row.index === 0 &&
        selectedOption?.signPath
      ) {
        const { x, y, width, height } = data.cell;
        const imageSize = height - 5;
        const centerX = x + 5;
        const centerY = y + (height - imageSize) / 2;

        doc.addImage(
          selectedOption?.signPath || "",
          "PNG",
          centerX,
          centerY,
          imageSize + 50,
          imageSize
        );
      }
    },
  });
};

export function generatePDF1(
  formInfo,
  inputStates,
  filteredInputFields,
  Factory,
  Shop,
  Product,
  selectedOption
) {
  const doc = new jsPDF({
    orientation: "p",
    unit: "pt",
    format: "a4",
    compress: true,
  });
  const totalWidth = doc.internal.pageSize.getWidth();
  const totalHeight = doc.internal.pageSize.getHeight();
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(
    config?.borderMagin + 2,
    config?.borderMagin + config.bordergap,
    totalWidth - config?.borderMagin * 2 - 2,
    totalHeight - config?.borderMagin * 2 - config.bordergap
  );

  generateStaticSection(doc, formInfo, inputStates);

  generateDynamicTableSection(
    doc,
    filteredInputFields,
    inputStates,
    Factory,
    Shop,
    Product
  );

  generateSignatureSectionPDF({
    doc,
    inputStates,
    selectedOption,
    formInfo,
  });

  doc.save(`form.pdf`);
}
